import * as DialogPrimitive from '@radix-ui/react-dialog';
import clsx from 'clsx';
import {
  Bot,
  CheckCircle2,
  ClipboardList,
  FileSearch,
  MessageSquare,
  Send,
  ShieldCheck,
  Sparkles,
  X,
} from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useJarvisAttentionSummary } from '../../services/jarvis';
import { Button } from '../ui/Button';
import { Skeleton } from '../ui/Skeleton';

export interface PennyCommandDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contextLabel?: string;
}

const modes = [
  { id: 'chat', label: 'Chat', icon: MessageSquare },
  { id: 'review', label: 'Review', icon: ClipboardList },
  { id: 'act', label: 'Act', icon: Sparkles },
  { id: 'audit', label: 'Audit', icon: ShieldCheck },
  { id: 'context', label: 'Context', icon: FileSearch },
] as const;

type Mode = (typeof modes)[number]['id'];

export function PennyCommandDrawer({ open, onOpenChange, contextLabel = 'Global command' }: PennyCommandDrawerProps) {
  const [mode, setMode] = useState<Mode>('chat');
  const [draft, setDraft] = useState('');
  const { data: summary, isLoading } = useJarvisAttentionSummary();

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-black/45 backdrop-blur-[2px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0" />
        <DialogPrimitive.Content
          className={clsx(
            'fixed bottom-0 right-0 top-0 z-50 flex w-full flex-col overflow-hidden',
            'workbench border-l border-copper/20 shadow-deep outline-none',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=open]:slide-in-from-right data-[state=closed]:slide-out-to-right',
            'sm:max-w-[520px] lg:max-w-[620px]',
          )}
        >
          <header className="border-b border-copper/10 p-4 sm:p-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="logo-coin flex h-11 w-11 items-center justify-center rounded-full">
                  <span className="font-display text-lg font-extrabold text-obsidian">P</span>
                </div>
                <div>
                  <DialogPrimitive.Title className="font-display text-lg font-bold text-parchment">
                    Penny Command
                  </DialogPrimitive.Title>
                  <DialogPrimitive.Description className="mt-1 font-mono text-[10px] uppercase tracking-[.18em] text-copper/80">
                    {contextLabel}
                  </DialogPrimitive.Description>
                </div>
              </div>
              <DialogPrimitive.Close className="rounded-xl p-2 text-dusty transition hover:bg-white/5 hover:text-parchment focus:outline-none focus:ring-2 focus:ring-copper/50">
                <X className="h-5 w-5" />
                <span className="sr-only">Close Penny command</span>
              </DialogPrimitive.Close>
            </div>

            <div className="mt-5 grid grid-cols-5 gap-1 rounded-2xl bg-obsidian/50 p-1">
              {modes.map((item) => {
                const Icon = item.icon;
                const active = mode === item.id;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setMode(item.id)}
                    className={clsx(
                      'flex flex-col items-center gap-1 rounded-xl px-2 py-2 text-[10px] font-semibold uppercase tracking-wide transition',
                      active ? 'bg-copper text-obsidian' : 'text-dusty hover:bg-white/5 hover:text-parchment',
                    )}
                  >
                    <Icon className="h-4 w-4" />
                    {item.label}
                  </button>
                );
              })}
            </div>
          </header>

          <main className="flex-1 overflow-y-auto p-4 scrollbar-penny sm:p-5">
            {mode === 'chat' && (
              <section className="space-y-4">
                <div className="ai-card rounded-2xl p-4">
                  <div className="mb-2 flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full bg-copper animate-copper-pulse" />
                    <p className="font-mono text-[10px] uppercase tracking-[.18em] text-copper">Penny says</p>
                  </div>
                  {isLoading ? (
                    <div className="space-y-2">
                      <Skeleton className="h-4 rounded" />
                      <Skeleton className="h-4 w-4/5 rounded" />
                    </div>
                  ) : (
                    <p className="text-sm leading-6 text-parchment/85">
                      {summary?.headline ?? 'Ask a question, review current work, or tell Penny what to prepare.'}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <p className="font-mono text-[10px] uppercase tracking-[.18em] text-dusty">Suggested questions</p>
                  {(summary?.suggestedQuestions?.length ? summary.suggestedQuestions : [
                    'What needs my attention right now?',
                    'Which projects are blocked?',
                    'Draft a client follow-up for review.',
                  ]).map((question) => (
                    <button
                      key={question}
                      type="button"
                      onClick={() => setDraft(question)}
                      className="block w-full rounded-2xl border border-copper/10 bg-white/[.03] px-4 py-3 text-left text-sm text-parchment transition hover:border-copper/25 hover:bg-copper/10"
                    >
                      {question}
                    </button>
                  ))}
                </div>
              </section>
            )}

            {mode === 'review' && (
              <section className="space-y-3">
                <p className="font-mono text-[10px] uppercase tracking-[.18em] text-dusty">Top work needing review</p>
                {isLoading ? (
                  [1, 2, 3].map((item) => <Skeleton key={item} className="h-20 rounded-2xl" />)
                ) : summary?.topObligations?.length ? (
                  summary.topObligations.slice(0, 6).map((item) => (
                    <Link
                      key={item.id}
                      to="/obligations"
                      onClick={() => onOpenChange(false)}
                      className="block rounded-2xl border border-copper/10 bg-white/[.03] p-4 transition hover:border-copper/25 hover:bg-white/[.05]"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-parchment">{item.title}</p>
                          <p className="mt-1 line-clamp-2 text-xs leading-5 text-dusty">{item.body ?? item.itemType}</p>
                        </div>
                        <span className="rounded-full bg-copper/10 px-2 py-1 font-mono text-[10px] uppercase text-copper">
                          {item.priority}
                        </span>
                      </div>
                    </Link>
                  ))
                ) : (
                  <EmptyDrawerState title="Nothing waiting for review" body="Open obligations will appear here when Penny or the system needs a human decision." />
                )}
              </section>
            )}

            {mode === 'act' && (
              <section className="grid gap-3">
                <DrawerAction title="Draft a reply" body="Prepare a client message for human review before sending." />
                <DrawerAction title="Create a task" body="Turn a note or project blocker into an assigned obligation." />
                <DrawerAction title="Summarize a project" body="Refresh a persisted project summary from recent activity." />
                <DrawerAction title="Find blocked jobs" body="Show projects that cannot move to the next stage yet." />
              </section>
            )}

            {mode === 'audit' && (
              <section className="space-y-3">
                <div className="audit-rail rounded-2xl bg-white/[.03] p-4">
                  <p className="text-sm font-semibold text-parchment">Audit lives beside action.</p>
                  <p className="mt-1 text-xs leading-5 text-dusty">
                    Recent tool calls, rejected actions, outbound queue decisions, and policy gates should appear here.
                  </p>
                </div>
                <Button asChild variant="secondary" className="w-full">
                  <Link to="/ai-log" onClick={() => onOpenChange(false)}>
                    Open full AI activity log
                  </Link>
                </Button>
              </section>
            )}

            {mode === 'context' && (
              <section className="space-y-3">
                <ContextLine label="Scope" value={contextLabel} />
                <ContextLine label="Authority" value="Switchboard-gated. High-impact actions require preview or approval." />
                <ContextLine label="Human-only" value="Invoices, payment requests, and discount negotiation." />
              </section>
            )}
          </main>

          <footer className="border-t border-copper/10 p-4 sm:p-5">
            <div className="flex items-end gap-3 rounded-2xl border border-copper/15 bg-obsidian/60 p-3">
              <Bot className="mb-2 h-5 w-5 shrink-0 text-copper" />
              <textarea
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                rows={2}
                placeholder="Ask Penny what needs attention, or tell it what to draft..."
                className="min-h-[44px] flex-1 resize-none bg-transparent text-sm text-parchment placeholder:text-dusty/50 focus:outline-none"
              />
              <button
                type="button"
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-copper text-obsidian transition hover:bg-copper-light disabled:opacity-40"
                disabled={!draft.trim()}
                aria-label="Submit command"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
            <p className="mt-2 text-center text-[11px] text-dusty/70">
              Penny should preview high-impact actions before anything changes.
            </p>
          </footer>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function DrawerAction({ title, body }: { title: string; body: string }) {
  return (
    <button
      type="button"
      className="rounded-2xl border border-copper/10 bg-white/[.03] p-4 text-left transition hover:border-copper/25 hover:bg-copper/10"
    >
      <div className="flex items-start gap-3">
        <CheckCircle2 className="mt-0.5 h-4 w-4 text-copper" />
        <div>
          <p className="text-sm font-semibold text-parchment">{title}</p>
          <p className="mt-1 text-xs leading-5 text-dusty">{body}</p>
        </div>
      </div>
    </button>
  );
}

function ContextLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-copper/10 bg-white/[.03] p-4">
      <p className="font-mono text-[10px] uppercase tracking-[.18em] text-dusty">{label}</p>
      <p className="mt-2 text-sm leading-6 text-parchment">{value}</p>
    </div>
  );
}

function EmptyDrawerState({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-2xl border border-dashed border-copper/20 bg-white/[.02] p-5 text-center">
      <p className="text-sm font-semibold text-parchment">{title}</p>
      <p className="mt-1 text-xs leading-5 text-dusty">{body}</p>
    </div>
  );
}
